/**
 * TanStack Query hooks for the Design Workflow module (`apps/api`
 * `/design-requests` and `/admin/design-workflow`).
 *
 * Wire types come from `@ithelpdesk/shared` (`design-workflow.ts`), the single
 * contract with the API. Authorisation is server-side only: this module never
 * decides who may do what. The UI renders action buttons from the
 * `availableActions` the API returns and the API re-checks every action; a
 * refused action surfaces here as an `ApiError`.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DesignMembersBulkCreateDto,
  DesignMembersBulkResultDto,
  CreateDesignRequestDto,
  DesignAction,
  DesignAssignDesignerDto,
  DesignAssignLeadDto,
  DesignAttachmentDto,
  DesignChangeRequestDto,
  DesignContentRevisionDto,
  DesignDashboardDto,
  DesignFinalContentChangesDto,
  DesignFinalRejectDto,
  DesignMemberUpdateDto,
  DesignRouteChangesDto,
  DesignSetEtaDto,
  DesignVersionFromUploadsDto,
  DesignWorkflowSettingsUpdateDto,
  DesignListView,
  DesignMemberCreateDto,
  DesignOptionalCommentDto,
  DesignPriority,
  DesignReasonDto,
  DesignRegionCreateDto,
  DesignRegionUpdateDto,
  DesignRequestCommentDto,
  DesignRequestDto,
  DesignRequestListDto,
  DesignRequestStatus,
  DesignTimelineEventDto,
  DesignWorkflowConfigDto,
  DesignWorkflowMeDto,
  UpdateDesignRequestDto,
} from '@ithelpdesk/shared';
import { apiGet, apiPost, NO_MUTATION_RETRY, request, shouldRetryQuery } from './client';
import type { PreviewableAttachment } from './attachments';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const designKeys = {
  all: ['design-workflow'] as const,
  me: () => ['design-workflow', 'me'] as const,
  dashboard: () => ['design-workflow', 'dashboard'] as const,
  lists: () => ['design-workflow', 'list'] as const,
  list: (params: DesignListParams) => ['design-workflow', 'list', params] as const,
  detail: (id: string) => ['design-workflow', 'detail', id] as const,
  timeline: (id: string) => ['design-workflow', 'timeline', id] as const,
  comments: (id: string) => ['design-workflow', 'comments', id] as const,
  config: () => ['design-workflow', 'config'] as const,
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * `GET /design-requests/me` — what the actor is in this workflow. Drives the
 * sidebar entry and the "New request" button. `enabled` lets the app shell
 * skip the call for an actor who does not hold the baseline permission.
 */
export function useDesignWorkflowMe(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: designKeys.me(),
    queryFn: ({ signal }) => apiGet<DesignWorkflowMeDto>('/design-requests/me', { signal }),
    retry: shouldRetryQuery,
    staleTime: 60_000,
    ...(options.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

export function useDesignDashboard() {
  return useQuery({
    queryKey: designKeys.dashboard(),
    queryFn: ({ signal }) => apiGet<DesignDashboardDto>('/design-requests/dashboard', { signal }),
    retry: shouldRetryQuery,
  });
}

export type DesignSortField = 'updatedAt' | 'createdAt' | 'dueDate' | 'number';

export interface DesignListParams {
  view: DesignListView;
  q?: string | undefined;
  status?: DesignRequestStatus | undefined;
  regionId?: string | undefined;
  priority?: DesignPriority | undefined;
  sort?: DesignSortField | undefined;
  order?: 'asc' | 'desc' | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

function toQuery(params: DesignListParams): string {
  const search = new URLSearchParams();
  search.set('view', params.view);
  if (params.q) search.set('q', params.q);
  if (params.status) search.set('status', params.status);
  if (params.regionId) search.set('regionId', params.regionId);
  if (params.priority) search.set('priority', params.priority);
  if (params.sort) search.set('sort', params.sort);
  if (params.order) search.set('order', params.order);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  return `?${search.toString()}`;
}

export function useDesignRequestList(params: DesignListParams) {
  return useQuery({
    queryKey: designKeys.list(params),
    queryFn: ({ signal }) => apiGet<DesignRequestListDto>(`/design-requests${toQuery(params)}`, { signal }),
    placeholderData: keepPreviousData,
    retry: shouldRetryQuery,
  });
}

export function useDesignRequest(id: string | undefined) {
  return useQuery({
    queryKey: designKeys.detail(id ?? ''),
    queryFn: ({ signal }) => apiGet<DesignRequestDto>(`/design-requests/${id}`, { signal }),
    enabled: Boolean(id),
    retry: shouldRetryQuery,
  });
}

export function useDesignTimeline(id: string | undefined) {
  return useQuery({
    queryKey: designKeys.timeline(id ?? ''),
    queryFn: ({ signal }) => apiGet<DesignTimelineEventDto[]>(`/design-requests/${id}/timeline`, { signal }),
    enabled: Boolean(id),
    retry: shouldRetryQuery,
  });
}

export function useDesignComments(id: string | undefined) {
  return useQuery({
    queryKey: designKeys.comments(id ?? ''),
    queryFn: ({ signal }) => apiGet<DesignRequestCommentDto[]>(`/design-requests/${id}/comments`, { signal }),
    enabled: Boolean(id),
    retry: shouldRetryQuery,
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Everything a request's state change can affect: its own detail/timeline, every list, the KPI numbers and the nav `me`. */
function useApplyRequestChange() {
  const qc = useQueryClient();
  return (changed: DesignRequestDto) => {
    qc.setQueryData(designKeys.detail(changed.id), changed);
    void qc.invalidateQueries({ queryKey: designKeys.lists() });
    void qc.invalidateQueries({ queryKey: designKeys.dashboard() });
    void qc.invalidateQueries({ queryKey: designKeys.timeline(changed.id) });
    void qc.invalidateQueries({ queryKey: designKeys.me() });
  };
}

export function useCreateDesignRequest() {
  const apply = useApplyRequestChange();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (input: CreateDesignRequestDto) => apiPost<DesignRequestDto>('/design-requests', input),
    onSuccess: apply,
  });
}

/** `PATCH /design-requests/:id` (action EDIT). `id` may be supplied per call for a draft that was created a moment ago. */
export function useUpdateDesignRequest(id?: string) {
  const apply = useApplyRequestChange();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: async (input: UpdateDesignRequestDto & { id?: string }) => {
      const { id: overrideId, ...body } = input;
      const { data } = await request<DesignRequestDto>(`/design-requests/${overrideId ?? id}`, { method: 'PATCH', body });
      return data;
    },
    onSuccess: apply,
  });
}

/** The workflow actions that are a plain JSON POST. UPLOAD_DESIGN is multipart; EDIT, COMMENT and ATTACH have their own calls. */
export type DesignPostAction = Exclude<DesignAction, 'EDIT' | 'UPLOAD_DESIGN' | 'COMMENT' | 'ATTACH'>;

export const DESIGN_ACTION_PATHS: Record<DesignPostAction, string> = {
  SUBMIT: 'submit',
  REGIONAL_APPROVE: 'regional-approve',
  REGIONAL_REQUEST_CHANGES: 'regional-request-changes',
  CONTENT_REVISE: 'content-versions',
  CONTENT_APPROVE: 'content-approve',
  CONTENT_REQUEST_CLARIFICATION: 'content-request-clarification',
  ASSIGN_DESIGN_LEAD: 'assign-design-lead',
  ASSIGN_DESIGNER: 'assign-designer',
  SUBMIT_DESIGN: 'submit-design',
  DESIGN_APPROVE: 'design-approve',
  DESIGN_REQUEST_CHANGES: 'design-request-changes',
  FINAL_CONTENT_APPROVE: 'final-content-approve',
  FINAL_CONTENT_REQUEST_CHANGES: 'final-content-request-changes',
  FINAL_APPROVE: 'final-approve',
  FINAL_REJECT: 'final-reject',
  REGIONAL_CONTENT_APPROVE: 'regional-content-approve',
  REGIONAL_CONTENT_RETURN: 'regional-content-return',
  REGIONAL_DESIGN_FORWARD: 'regional-design-forward',
  REGIONAL_DESIGN_RETURN: 'regional-design-return',
  ROUTE_CHANGES: 'route-changes',
  SHARE_FOR_BUSINESS_FEEDBACK: 'share-for-feedback',
  BUSINESS_ACCEPT_FOR_APPROVAL: 'business-accept',
  BUSINESS_FEEDBACK_CHANGES: 'business-feedback-changes',
  SET_ETA: 'eta',
  SEND_TO_BUSINESS: 'send-to-business',
  BUSINESS_CONFIRM: 'business-confirm',
  BUSINESS_REQUEST_CHANGES: 'request-further-changes',
  RELEASE: 'release',
  CANCEL: 'cancel',
};

/** The request body each action takes. `undefined` = no body. */
export interface DesignActionBodies {
  SUBMIT: undefined;
  REGIONAL_APPROVE: DesignOptionalCommentDto;
  REGIONAL_REQUEST_CHANGES: DesignReasonDto;
  CONTENT_REVISE: DesignContentRevisionDto;
  CONTENT_APPROVE: DesignOptionalCommentDto;
  CONTENT_REQUEST_CLARIFICATION: DesignReasonDto;
  ASSIGN_DESIGN_LEAD: DesignAssignLeadDto;
  ASSIGN_DESIGNER: DesignAssignDesignerDto;
  SUBMIT_DESIGN: DesignOptionalCommentDto;
  DESIGN_APPROVE: DesignOptionalCommentDto;
  DESIGN_REQUEST_CHANGES: DesignReasonDto;
  FINAL_CONTENT_APPROVE: DesignOptionalCommentDto;
  FINAL_CONTENT_REQUEST_CHANGES: DesignFinalContentChangesDto;
  FINAL_APPROVE: DesignOptionalCommentDto;
  FINAL_REJECT: DesignFinalRejectDto;
  REGIONAL_CONTENT_APPROVE: DesignOptionalCommentDto;
  REGIONAL_CONTENT_RETURN: DesignReasonDto;
  REGIONAL_DESIGN_FORWARD: DesignOptionalCommentDto;
  REGIONAL_DESIGN_RETURN: DesignReasonDto;
  ROUTE_CHANGES: DesignRouteChangesDto;
  SHARE_FOR_BUSINESS_FEEDBACK: DesignOptionalCommentDto;
  BUSINESS_ACCEPT_FOR_APPROVAL: DesignOptionalCommentDto;
  BUSINESS_FEEDBACK_CHANGES: DesignChangeRequestDto;
  SET_ETA: DesignSetEtaDto;
  SEND_TO_BUSINESS: DesignOptionalCommentDto;
  BUSINESS_CONFIRM: DesignOptionalCommentDto;
  BUSINESS_REQUEST_CHANGES: DesignChangeRequestDto;
  RELEASE: DesignOptionalCommentDto;
  CANCEL: DesignReasonDto;
}

export type DesignActionInput = {
  [A in DesignPostAction]: undefined extends DesignActionBodies[A]
    ? { action: A; body?: DesignActionBodies[A] }
    : { action: A; body: DesignActionBodies[A] };
}[DesignPostAction];

/**
 * One mutation for every JSON workflow action: `mutate({ action, body })`
 * posts to that action's endpoint and refreshes the request, its timeline,
 * the lists and the dashboard. Callers disable their button while `isPending`.
 */
export function useDesignAction(id: string) {
  const apply = useApplyRequestChange();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (input: DesignActionInput) =>
      apiPost<DesignRequestDto>(`/design-requests/${id}/${DESIGN_ACTION_PATHS[input.action]}`, input.body),
    onSuccess: apply,
  });
}

/** `POST :id/submit` for a request whose id is only known at call time (the form creates a draft, then submits it). */
export function useSubmitDesignRequest() {
  const apply = useApplyRequestChange();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (id: string) => apiPost<DesignRequestDto>(`/design-requests/${id}/submit`),
    onSuccess: apply,
  });
}

/** `POST :id/design-versions/from-uploads` — one design version made of 1..5 files already uploaded straight to storage. */
export function useCreateDesignVersionFromUploads(id: string) {
  const apply = useApplyRequestChange();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (input: DesignVersionFromUploadsDto) => apiPost<DesignRequestDto>(`/design-requests/${id}/design-versions/from-uploads`, input),
    onSuccess: apply,
  });
}

/** `POST :id/design-versions` — multipart (legacy single-file path; the UI uses direct uploads + from-uploads). `note` is appended BEFORE the file so the server sees it first. */
export function uploadDesignVersion(id: string, file: File, note?: string): Promise<DesignRequestDto> {
  const form = new FormData();
  if (note && note.trim() !== '') form.append('note', note.trim());
  form.append('file', file, file.name);
  return apiPost<DesignRequestDto>(`/design-requests/${id}/design-versions`, form);
}

export function useUploadDesignVersion(id: string) {
  const apply = useApplyRequestChange();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (input: { file: File; note?: string }) => uploadDesignVersion(id, input.file, input.note),
    onSuccess: apply,
  });
}

export function useAddDesignComment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (body: string) => apiPost<DesignRequestCommentDto>(`/design-requests/${id}/comments`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: designKeys.comments(id) });
      void qc.invalidateQueries({ queryKey: designKeys.timeline(id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

/** Uploads one reference/content file (multipart field `file`). The server sniffs the content and enforces size/type/count. */
export function uploadDesignAttachment(id: string, file: File): Promise<DesignAttachmentDto> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiPost<DesignAttachmentDto>(`/design-requests/${id}/attachments`, form);
}

export async function deleteDesignAttachment(id: string, attachmentId: string): Promise<void> {
  await request<undefined>(`/design-requests/${id}/attachments/${attachmentId}`, { method: 'DELETE' });
}

function useRefreshAttachments(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: designKeys.detail(id) });
    void qc.invalidateQueries({ queryKey: designKeys.timeline(id) });
  };
}

export function useUploadDesignAttachment(id: string) {
  const refresh = useRefreshAttachments(id);
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (file: File) => uploadDesignAttachment(id, file),
    onSuccess: refresh,
  });
}

export function useDeleteDesignAttachment(id: string) {
  const refresh = useRefreshAttachments(id);
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (attachmentId: string) => deleteDesignAttachment(id, attachmentId),
    onSuccess: refresh,
  });
}

/** Authorised streaming endpoint used for preview (`?inline=1`) and download. */
export function designAttachmentContentPath(id: string, attachmentId: string, inline = true): string {
  return `/design-requests/${id}/attachments/${attachmentId}/content${inline ? '?inline=1' : ''}`;
}

/** The design attachment, in the shape the shared preview modal understands. */
export function toPreviewable(a: DesignAttachmentDto): PreviewableAttachment {
  return {
    id: a.id,
    originalFilename: a.fileName,
    byteSize: a.sizeBytes,
    contentType: a.mimeType,
    sniffedMime: a.mimeType,
    createdAt: a.uploadedAt,
  };
}

// ---------------------------------------------------------------------------
// Admin configuration (Admin -> Settings -> Design Workflow)
// ---------------------------------------------------------------------------

export function useDesignWorkflowConfig(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: designKeys.config(),
    queryFn: ({ signal }) => apiGet<DesignWorkflowConfigDto>('/admin/design-workflow', { signal }),
    retry: shouldRetryQuery,
    ...(options.enabled !== undefined ? { enabled: options.enabled } : {}),
  });
}

function useRefreshConfig() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: designKeys.config() });
    // Regions and roles feed the requester's region list, assignment choices and the sidebar entry.
    void qc.invalidateQueries({ queryKey: designKeys.me() });
  };
}

export function useCreateDesignRegion() {
  const refresh = useRefreshConfig();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (input: DesignRegionCreateDto) => apiPost<unknown>('/admin/design-workflow/regions', input),
    onSuccess: refresh,
  });
}

export function useUpdateDesignRegion() {
  const refresh = useRefreshConfig();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: async (input: { id: string; changes: DesignRegionUpdateDto }) => {
      const { data } = await request<unknown>(`/admin/design-workflow/regions/${input.id}`, { method: 'PATCH', body: input.changes });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useAddDesignMember() {
  const refresh = useRefreshConfig();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (input: DesignMemberCreateDto) => apiPost<unknown>('/admin/design-workflow/members', input),
    onSuccess: refresh,
  });
}

/** Adds several people to one role in one call (all-or-nothing; people who already hold the role are reported as skipped). */
export function useAddDesignMembers() {
  const refresh = useRefreshConfig();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: (input: DesignMembersBulkCreateDto) => apiPost<DesignMembersBulkResultDto>('/admin/design-workflow/members/bulk', input),
    onSuccess: refresh,
  });
}

export function useRemoveDesignMember() {
  const refresh = useRefreshConfig();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: async (memberId: string) => {
      await request<undefined>(`/admin/design-workflow/members/${memberId}`, { method: 'DELETE' });
    },
    onSuccess: refresh,
  });
}

/** `PATCH /admin/design-workflow/settings` — the final approval policy (applies to rounds opened from now on). */
export function useUpdateDesignWorkflowSettings() {
  const refresh = useRefreshConfig();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: async (input: DesignWorkflowSettingsUpdateDto) => {
      const { data } = await request<unknown>('/admin/design-workflow/settings', { method: 'PATCH', body: input });
      return data;
    },
    onSuccess: refresh,
  });
}

/** `PATCH /admin/design-workflow/members/:id` — only a final approver's `isRequired` can change. */
export function useUpdateDesignMember() {
  const refresh = useRefreshConfig();
  return useMutation({
    retry: NO_MUTATION_RETRY,
    mutationFn: async (input: { id: string; changes: DesignMemberUpdateDto }) => {
      const { data } = await request<unknown>(`/admin/design-workflow/members/${input.id}`, { method: 'PATCH', body: input.changes });
      return data;
    },
    onSuccess: refresh,
  });
}
